/**
 * 知识卡 schema（zod）
 *
 * 知识卡是《传灯》的内容原子：技能树节点、答题题目、错题复习、
 * 好友明信片全部以它为源。卡库在 content/cards/ 以 JSON 维护，
 * 经 tools/validate-cards 校验后打进前端包体 + R2 增量分发。
 *
 * 生产流程（GDD 12.3 三道关）：LLM 起草 → 人工/教师审核 → 脚本校验。
 */

import { z } from "zod";

export const subjectSchema = z.enum(["biology", "geography", "history", "math"]);
export type Subject = z.infer<typeof subjectSchema>;

export const gradeSchema = z.enum(["primary-high", "junior"]);
export type Grade = z.infer<typeof gradeSchema>;

export const torchKindSchema = z.enum(["craft", "life", "math", "culture"]);

export const quizSchema = z.object({
  question: z.string().min(5).max(200),
  /** 选择题 4 选项（后续题型：判断、排序，另行扩展） */
  options: z.array(z.string().min(1).max(80)).length(4),
  answerIndex: z.number().int().min(0).max(3),
  /** 答题后的即时讲解：讲"为什么"，不是重复题干 */
  explanation: z.string().min(5).max(300),
});

export const knowledgeCardSchema = z
  .object({
    /** 命名规范：<subject>-<三位序号>，如 biology-001 */
    id: z
      .string()
      .regex(/^(biology|geography|history|math)-\d{3}$/, "id 必须形如 biology-001"),
    title: z.string().min(2).max(30),
    /** 一句话摘要，列表页展示 */
    summary: z.string().min(5).max(120),
    /** 正文：60~90 秒阅读量，Markdown，约 200~400 字 */
    body: z.string().min(50).max(1200),
    subject: subjectSchema,
    /** 适配学段 */
    grade: gradeSchema,
    /** 课程映射标签，如 "人教版生物八下·传染病与免疫" */
    curriculum: z.array(z.string().min(2)).min(1).max(3),
    /** 所属火种类别 */
    torch: torchKindSchema,
    /** 关联技能树节点 id（可空：非技能树卡片） */
    skillNodeId: z.string().nullable().default(null),
    /** 即时小测（复习与明信片复用此题） */
    quiz: quizSchema,
    /** 错题复习间隔（天），默认 1/3/7/15 */
    reviewStages: z.array(z.number().int().positive()).default([1, 3, 7, 15]),
    /** 审核状态：LLM 起草为 draft，人工确认后为 approved */
    status: z.enum(["draft", "approved"]).default("draft"),
    /** 审核人（人工二道关的署名） */
    reviewedBy: z.string().nullable().default(null),
  })
  .strict();

export type KnowledgeCard = z.infer<typeof knowledgeCardSchema>;

/** 上线白名单：只有 approved 的卡可以进入游戏 */
export function isPublishable(card: KnowledgeCard): boolean {
  return card.status === "approved" && card.reviewedBy !== null;
}
